import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight, Users, Clock, Trophy, MapPin, Calendar, Gamepad2, Target, GraduationCap } from 'lucide-react';
import api from '../services/api';
import { FACILITY_IMAGES, SPORT_LABELS, DEFAULT_VENUE_IMAGE } from '../config/venueConfig';
import { EventCard } from './EventsPage';
import { CoachingCard } from './CoachingPage';
import { HorizontalScroller } from './HorizontalScroller';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExplorePage = () => {
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState([]);
  const [games, setGames] = useState([]);
  const [events, setEvents] = useState([]);
  const [coaching, setCoaching] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    api.getPublicFacilities().then(setFacilities).catch(() => {});
    fetchGames();
    fetchEvents();
    fetchCoaching();
  }, []);

  const fetchGames = async () => {
    try {
      const res = await fetch(`${API_URL}/api/community-games/public`);
      if (res.ok) {
        const data = await res.json();
        setGames(Array.isArray(data) ? data.filter(g => g.status === 'open' || g.status === 'upcoming').slice(0, 5) : []);
      }
    } catch { /* silent */ }
  };

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${API_URL}/api/events/public/list?status=open`);
      if (res.ok) {
        const data = await res.json();
        setEvents(Array.isArray(data) ? data.slice(0, 5) : []);
      }
    } catch { /* silent */ }
  };

  const fetchCoaching = async () => {
    try {
      const res = await fetch(`${API_URL}/api/coaching/public/list?status=open&limit=5`);
      if (res.ok) {
        const data = await res.json();
        setCoaching(Array.isArray(data) ? data.slice(0, 5) : []);
      }
    } catch { /* silent */ }
  };

  const filtered = facilities.filter(f =>
    f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (f.location || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0">
      {/* HERO compact */}
      <section className="relative py-12 px-4 bg-[#0A2C93] overflow-hidden" data-testid="explore-hero">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1200&q=60" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-md mx-auto text-center">
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-4 tracking-tight">Explore</h1>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search games, venues, experiences"
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white text-slate-900 placeholder-slate-400 font-body text-sm focus:outline-none focus:ring-2 focus:ring-[#00D650]"
              data-testid="explore-search"
            />
          </div>
        </div>
      </section>

      {/* PRIMARY ACTION STRIP */}
      <section className="py-6 px-4" data-testid="action-strip">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-4 gap-2 sm:gap-3">
            {[
              { label: 'Book Venue', Icon: Calendar, onClick: () => navigate('/book'), active: true },
              { label: 'Join Game', Icon: Gamepad2, onClick: () => document.getElementById('games-section')?.scrollIntoView({ behavior: 'smooth' }), active: true },
              { label: 'Events', Icon: Target, onClick: () => navigate('/events'), active: true },
              { label: 'Coaching', Icon: GraduationCap, onClick: () => navigate('/coaching'), active: true },
            ].map(item => (
              <button
                key={item.label}
                onClick={item.active ? item.onClick : undefined}
                className={`relative flex flex-col items-center gap-1.5 py-3 px-2 rounded-2xl border transition-all duration-200 text-center ${
                  item.active
                    ? 'border-[#0A2C93]/10 bg-white hover:-translate-y-0.5 hover:shadow-md cursor-pointer'
                    : 'border-slate-100 bg-slate-50 cursor-not-allowed opacity-60'
                }`}
                style={item.active ? { boxShadow: '0 4px 15px rgb(0 0 0 / 0.04)' } : {}}
                data-testid={`action-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.active ? 'bg-[#0A2C93]/5' : 'bg-slate-100'}`}>
                  <item.Icon className={`w-5 h-5 ${item.active ? 'text-[#0A2C93]' : 'text-slate-400'}`} />
                </div>
                <span className="font-heading font-semibold text-[11px] sm:text-xs text-[#0A2C93] leading-tight">{item.label}</span>
                {!item.active && (
                  <span className="absolute -top-1 -right-1 px-1 py-0.5 rounded-full bg-[#0A2C93] text-[8px] font-heading font-bold text-white">SOON</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* VENUES SECTION */}
      <section className="py-6 px-4" data-testid="venues-section">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-[#0A2C93]">Venues</h2>
            <span className="font-body text-xs text-[#737373]">{filtered.length} venues</span>
          </div>
        </div>
        <div className="max-w-5xl mx-auto overflow-visible">
          <HorizontalScroller className="gap-3 pb-4" testId="explore-venues-scroller">
            {filtered.map(f => (
              <VenueScrollCard key={f.id} facility={f} onClick={() => navigate(`/venue/${f.slug || f.id}`)} />
            ))}
          </HorizontalScroller>
        </div>
      </section>

      {/* JOIN A GAME — horizontal scroll */}
      {games.length > 0 && (
        <section id="games-section" className="py-6 px-4 bg-[#F8FAFC]" data-testid="games-preview">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-lg sm:text-xl text-[#0A2C93]">Join a Game</h2>
              <button
                onClick={() => navigate('/join-a-game')}
                className="text-xs font-heading font-semibold text-[#0A2C93] hover:underline"
                data-testid="see-all-games"
              >
                See all &rarr;
              </button>
            </div>
            <HorizontalScroller className="gap-3 pb-2" testId="explore-games-scroller">
              {games.map(game => (
                <div key={game.id || game._id} className="flex-shrink-0 snap-start" style={{ width: '280px' }}>
                  <GameCard game={game} onClick={() => {
                    const venueSlug = game.venue_slug || game.venue_name || 'venue';
                    navigate(`/community/${venueSlug}/game/${game.id || game._id}`);
                  }} />
                </div>
              ))}
            </HorizontalScroller>
          </div>
        </section>
      )}

      {/* UPCOMING EVENTS — horizontal scroll */}
      {events.length > 0 && (
        <section className="py-6 px-4" data-testid="upcoming-events-section">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-lg sm:text-xl text-[#0A2C93]">Upcoming Events</h2>
              <button
                onClick={() => navigate('/events')}
                className="text-xs font-heading font-semibold text-[#0A2C93] hover:underline"
                data-testid="see-all-events"
              >
                See all &rarr;
              </button>
            </div>
            <HorizontalScroller className="gap-3 pb-2" testId="explore-events-scroller">
              {events.map(ev => (
                <div key={ev.id} className="flex-shrink-0 snap-start" style={{ width: '260px' }}>
                  <EventCard event={ev} onClick={() => navigate(`/events/${ev.slug}`)} />
                </div>
              ))}
            </HorizontalScroller>
          </div>
        </section>
      )}

      {/* COACHING — horizontal scroll */}
      {coaching.length > 0 && (
        <section className="py-6 px-4 bg-[#F8FAFC]" data-testid="coaching-section">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-lg sm:text-xl text-[#0A2C93]">Coaching</h2>
              <button
                onClick={() => navigate('/coaching')}
                className="text-xs font-heading font-semibold text-[#0A2C93] hover:underline"
                data-testid="see-all-coaching"
              >
                See all &rarr;
              </button>
            </div>
            <HorizontalScroller className="gap-3 pb-2" testId="explore-coaching-scroller">
              {coaching.map(p => (
                <div key={p.id} className="flex-shrink-0 snap-start" style={{ width: '260px' }}>
                  <CoachingCard programme={p} onClick={() => navigate(`/coaching/${p.slug}`)} />
                </div>
              ))}
            </HorizontalScroller>
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer className="bg-[#0A2C93] text-white py-8 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <span className="font-logo text-xl font-black">PlaySpaces</span>
          <div className="h-px w-12 mx-auto mt-2 mb-3 bg-white/20" />
          <p className="font-body text-xs text-white/50">&copy; 2026 PlaySpaces. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

/* ---------- Sub-components ---------- */

const VenueScrollCard = ({ facility, onClick }) => {
  const img = FACILITY_IMAGES[facility.id] || DEFAULT_VENUE_IMAGE;
  const sports = (facility.sports || []).map(s => s === 'pickle' ? 'pickleball' : s);

  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 w-[220px] sm:w-[260px] snap-start bg-white border border-slate-100 rounded-2xl overflow-hidden text-left hover:-translate-y-1 transition-all duration-200"
      style={{ boxShadow: '0 8px 30px rgb(0 0 0 / 0.04)' }}
      data-testid={`explore-venue-${facility.id}`}
    >
      <div className="relative h-32">
        <img src={img} alt={facility.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
      </div>
      <div className="p-3">
        <h3 className="font-heading font-bold text-sm text-[#0A2C93] mb-1">{facility.name}</h3>
        <div className="flex flex-wrap gap-1 mb-2">
          {sports.map(s => (
            <span key={s} className="px-2 py-0.5 rounded-full bg-[#0A2C93]/5 text-[10px] font-heading font-semibold text-[#0A2C93]">
              {SPORT_LABELS[s] || s}
            </span>
          ))}
        </div>
        <span className="flex items-center gap-1 text-xs font-heading font-semibold text-[#00D650]">
          View Venue <ChevronRight className="w-3 h-3" />
        </span>
      </div>
    </button>
  );
};

const GameCard = ({ game, onClick }) => {
  const formatDate = (d) => {
    try { return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }); }
    catch { return d; }
  };

  const spotsLeft = (game.spots_left != null) ? game.spots_left : Math.max(0, (game.max_players || 0) - (game.players_filled || game.participants?.length || 0));

  return (
    <button
      onClick={onClick}
      className="w-full h-full bg-white border border-slate-100 rounded-2xl p-4 text-left hover:-translate-y-0.5 transition-all duration-200"
      style={{ boxShadow: '0 4px 15px rgb(0 0 0 / 0.04)' }}
      data-testid={`game-card-${game.id || game._id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <span className="px-2 py-0.5 rounded-full bg-[#00D650]/10 text-[10px] font-heading font-bold text-[#00D650] uppercase">
          {game.sport || 'Football'}
        </span>
        {game.skill_level && (
          <span className="flex items-center gap-0.5 text-[10px] font-body text-[#737373]">
            <Trophy className="w-3 h-3" /> {game.skill_level}
          </span>
        )}
      </div>
      <h3 className="font-heading font-bold text-sm text-[#0A2C93] mb-1">
        {game.sport || 'Football'} at {game.venue_name || 'Venue'}
      </h3>
      <div className="flex items-center gap-3 text-[11px] font-body text-[#737373] mb-3">
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {game.start_time} - {game.end_time}</span>
        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {formatDate(game.date)}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1 text-xs font-body text-[#737373]">
          <Users className="w-3 h-3" /> {spotsLeft > 0 ? `${spotsLeft} spots left` : 'Full'}
        </span>
        <span className="text-xs font-heading font-bold text-[#0A2C93]">
          ₹{game.cost_per_player || 0}
        </span>
      </div>
    </button>
  );
};

export default ExplorePage;
